import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter } from 'iconsax-react';
import CustomModal from '../../../atoms/CustomModal';
import PillCheckboxGroup from '../../../atoms/PillCheckboxGroup';
import Button from '../../../atoms/Button';
import CustomDatePicker from '../../../atoms/CustomDatePicker';
import { Dayjs } from 'dayjs';

interface ProductsFilterProps {
  sellerName?: string;
  onFilterChange?: (filters: {
    stockStatus: string[];
    publishStatus: string[];
    fromDate?: string;
    toDate?: string;
  }) => void;
}

export default function ProductsFilter({ sellerName, onFilterChange }: ProductsFilterProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<string[]>([]);
  const [selectedPublish, setSelectedPublish] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);

  const STOCK_STATUS_OPTIONS = [
    { label: t('sellers.products.inStock'), value: 'IN_STOCK' },
    { label: t('sellers.products.outOfStock'), value: 'OUT_OF_STOCK' },
    { label: t('sellers.products.lowStock'), value: 'LOW_STOCK' },
  ];

  const PUBLISH_STATUS_OPTIONS = [
    { label: t('sellers.products.published'), value: 'true' },
    { label: t('sellers.products.unpublished'), value: 'false' },
  ];

  const handleApply = () => {
    onFilterChange?.({
      stockStatus: selectedStock,
      publishStatus: selectedPublish,
      fromDate: fromDate ? fromDate.format('YYYY-MM-DD') : undefined,
      toDate: toDate ? toDate.format('YYYY-MM-DD') : undefined,
    });
    setIsOpen(false);
  };

  const handleReset = () => {
    setSelectedStock([]);
    setSelectedPublish([]);
    setFromDate(null);
    setToDate(null);
    onFilterChange?.({
      stockStatus: [],
      publishStatus: [],
      fromDate: undefined,
      toDate: undefined,
    });
    setIsOpen(false);
  };

  return (
    <div data-testid="store-products-filter">
      <Button
        variant="outline"
        icon={<Filter size={18} />}
        data-testid="store-products-filter-btn"
        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm text-sm font-semibold text-[#090909] dark:text-white h-[44px]"
        onClick={() => setIsOpen(true)}
      >
        {t('sellers.products.filter')}
      </Button>

      <CustomModal
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        title={t('sellers.products.filterTitle')}
        subTitle={
          <span>
            {t('sellers.products.filterSubtitlePrefix')}
            <strong className="font-bold text-gray-900 dark:text-white">{sellerName || ''}</strong>
            {t('sellers.products.filterSubtitleSuffix')}
          </span>
        }
        centered
        width={500}
      >
        <div className="flex flex-col gap-6" data-testid="store-products-filter-form">
          {/* Date Range */}
          <div className="flex gap-4 w-full" data-testid="filter-date-range">
            <div className="w-1/2 flex flex-col gap-2">
              <CustomDatePicker
                id="filter-date-from"
                label={t('product.dateFrom')}
                placeholder={t('product.enterPlaceholder')}
                value={fromDate}
                onChange={(date) => setFromDate(date as Dayjs | null)}
                className="w-full"
              />
            </div>
            <div className="w-1/2 flex flex-col gap-2">
              <CustomDatePicker
                id="filter-date-to"
                label={t('product.dateTo')}
                placeholder={t('product.enterPlaceholder')}
                value={toDate}
                onChange={(date) => setToDate(date as Dayjs | null)}
                className="w-full"
              />
            </div>
          </div>

          {/* Stock Status */}
          <PillCheckboxGroup
            label={t('sellers.products.stockStatus')}
            options={STOCK_STATUS_OPTIONS}
            value={selectedStock}
            onChange={setSelectedStock}
            dataTestId="filter-stock-status"
          />

          {/* Publish Status */}
          <PillCheckboxGroup
            label={t('sellers.products.publishStatus')}
            options={PUBLISH_STATUS_OPTIONS}
            value={selectedPublish}
            onChange={setSelectedPublish}
            dataTestId="filter-publish-status"
          />

          {/* Footer Buttons */}
          <div className="flex gap-4 mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <Button
              variant="outline"
              onClick={handleReset}
              data-testid="store-products-filter-reset-btn"
              className="w-1/2"
            >
              {t('sellers.products.resetFilter')}
            </Button>
            <Button
              variant="primary"
              onClick={handleApply}
              data-testid="store-products-filter-submit-btn"
              className="w-1/2"
            >
              {t('sellers.products.filter')}
            </Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
